import { useMemo, useState, type FormEvent } from 'react';
import { Clock } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { periodRange } from '@/features/teacher/components/timetable/timetableCopy';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import type { TimetablePeriod } from '@/types/timetable';
import type { PeriodPayload } from '@/types/timetableAdmin';
import { instituteTimetableApi } from '../api/timetable.api';
import { describeMinutes, durationMinutes, periodNumberToSend } from '../timetableWeek';

/**
 * What an institute calls a row of its day.
 *
 * A period is a row of the *institute's* day, shared by every class — which is
 * why this form no longer asks for a class, a section, a subject, a teacher, a
 * date range or a colour. It asked for all six, sent none of them, and the two
 * that looked like they worked were the worst of it: picking a subject quietly
 * placed it on six weekdays at once, swallowing every clash the server
 * reported, and the date range implied a period could apply to one fortnight
 * when the timetable recurs all year. Placing a lesson is a click on a cell of
 * the grid, where the clash can be shown against the slot it belongs to.
 *
 * `label` is what carries "Assembly", "Games period", "Lunch break" — the
 * institute's own name for the row, printed instead of "Period 3".
 */

/** The named rows an Indian institute actually runs, and what each one is. */
const PERIOD_PRESETS = [
  { value: 'TEACHING', label: 'Teaching period', kind: 'TEACHING', name: '' },
  // No class is taught at assembly, so it is a break: one band across the week, like lunch.
  { value: 'ASSEMBLY', label: 'Assembly', kind: 'BREAK', name: 'Assembly' },
  { value: 'GAMES', label: 'Games / PE period', kind: 'TEACHING', name: 'Games' },
  { value: 'LAB', label: 'Lab / practical period', kind: 'TEACHING', name: 'Lab' },
  { value: 'LIBRARY', label: 'Library period', kind: 'TEACHING', name: 'Library' },
  { value: 'SHORT_BREAK', label: 'Short break (recess)', kind: 'BREAK', name: 'Short break' },
  { value: 'LUNCH', label: 'Lunch break', kind: 'BREAK', name: 'Lunch break' },
] as const;

type PresetValue = (typeof PERIOD_PRESETS)[number]['value'];

export interface CreatePeriodModalProps {
  /** The day as it stands, so the next free number and time can be suggested. */
  existingPeriods: TimetablePeriod[];
  onClose: () => void;
  onSaved: () => void;
}

export function CreatePeriodModal({ existingPeriods, onClose, onSaved }: CreatePeriodModalProps) {
  /**
   * The first number no teaching period is using, and the time the day
   * currently ends — which is where the next row of it almost always starts.
   * A break's number is internal and the server moves it aside, so it does not
   * make the suggestion skip a number the grid shows as free.
   */
  const suggestion = useMemo(() => {
    const taken = new Set(
      existingPeriods.filter((period) => period.kind === 'TEACHING').map((period) => period.periodNumber),
    );
    let next = 1;
    while (taken.has(next)) next += 1;

    const latest = existingPeriods.reduce(
      (last, period) => (period.endTime > last ? period.endTime : last),
      '',
    );

    return { periodNumber: next, startTime: latest || '09:00' };
  }, [existingPeriods]);

  const [preset, setPreset] = useState<PresetValue>('TEACHING');
  const [periodNumber, setPeriodNumber] = useState(String(suggestion.periodNumber));
  const [label, setLabel] = useState('');
  const [startTime, setStartTime] = useState(suggestion.startTime);
  const [endTime, setEndTime] = useState(addMinutes(suggestion.startTime, 45));
  const [isSaving, setIsSaving] = useState(false);

  const chosen = PERIOD_PRESETS.find((item) => item.value === preset) ?? PERIOD_PRESETS[0];
  const isBreak = chosen.kind === 'BREAK';
  const duration = durationMinutes(startTime, endTime);

  function handlePresetChange(value: PresetValue) {
    const next = PERIOD_PRESETS.find((item) => item.value === value) ?? PERIOD_PRESETS[0];
    setPreset(value);
    // The preset's own name replaces whatever the last one suggested, but a
    // name the reader typed themselves is theirs to keep.
    if (label === '' || label === chosen.name) setLabel(next.name);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (isSaving) return;

    const trimmedLabel = label.trim();
    if (isBreak && !trimmedLabel) {
      toast.error('Name the break', 'A break needs a name — it has no number to go by.');
      return;
    }

    setIsSaving(true);
    try {
      const payload: PeriodPayload = {
        // Field by field, exactly what the API takes.
        periodNumber: periodNumberToSend(periodNumber, isBreak),
        kind: isBreak ? 'BREAK' : 'TEACHING',
        label: trimmedLabel || undefined,
        startTime,
        endTime,
      };

      const created = await instituteTimetableApi.createPeriod(payload);
      toast.success(
        `${created.label ?? `Period ${created.periodNumber}`} added`,
        `${periodRange(created.startTime, created.endTime)} — every class now has this row.`,
      );
      onSaved();
      onClose();
    } catch (caught) {
      toast.error(
        'Could not add that period',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Add a period"
      description="Bell times are shared by every class in the institute."
      size="md"
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            type="submit"
            form="create-period-form"
            isLoading={isSaving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
          >
            Add period
          </Button>
        </>
      }
    >
      <form id="create-period-form" onSubmit={save} className="flex flex-col gap-4 py-1">
        <Select
          label="What is this row of the day?"
          options={PERIOD_PRESETS.map((item) => ({ value: item.value, label: item.label }))}
          value={preset}
          onChange={(event) => handlePresetChange(event.target.value as PresetValue)}
        />

        <div className={cn('grid grid-cols-1 gap-4', !isBreak && 'sm:grid-cols-2')}>
          {/* A break has no number to choose: it is placed by its start time. */}
          {!isBreak && (
            <Input
              label="Period number"
              type="number"
              min={1}
              max={20}
              required
              value={periodNumber}
              onChange={(event) => setPeriodNumber(event.target.value)}
              hint="How the institute counts it. Two rows cannot share a number."
            />
          )}

          <Input
            label={isBreak ? 'Name' : 'Name (optional)'}
            placeholder={isBreak ? 'e.g. Lunch break' : 'e.g. Assembly'}
            maxLength={40}
            required={isBreak}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            hint={isBreak ? undefined : `Left empty it reads as "Period ${periodNumber || '3'}".`}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Starts"
            type="time"
            required
            value={startTime}
            onChange={(event) => {
              setStartTime(event.target.value);
              // The end follows the start until the reader moves it themselves,
              // which is what makes adding six periods in a row bearable.
              if (event.target.value >= endTime) setEndTime(addMinutes(event.target.value, 45));
            }}
          />
          <Input
            label="Ends"
            type="time"
            required
            value={endTime}
            onChange={(event) => setEndTime(event.target.value)}
            error={duration <= 0 ? 'A period cannot end before it starts' : undefined}
          />
        </div>

        <p className="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400">
          <Clock className="h-3.5 w-3.5 shrink-0" />
          <span>
            {isBreak ? chosen.label : `Period ${periodNumber || '—'}`} ·{' '}
            {periodRange(startTime, endTime)} · {describeMinutes(duration)}
          </span>
        </p>
      </form>
    </Modal>
  );
}

/** "09:00" plus 45 minutes, clamped to the end of the day. */
function addMinutes(time: string, minutes: number): string {
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match) return time;

  const total = Math.min(Number(match[1]) * 60 + Number(match[2]) + minutes, 23 * 60 + 59);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}
